import { useMemo, useRef, useState, type ElementType } from 'react';
import { AppLayout } from '@/components/AppLayout';
import { showAppError } from '@/lib/errors';
import { shouldUsePaidCyclePlanChange } from '@/lib/billing/planChangeRouting';
import { HIRE_PAGE_CONTAINER, HIRE_SECTION_SPACING } from '@/lib/hireResponsive';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import { Progress } from '@/components/ui/progress';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { useAuth } from '@/contexts/AuthContext';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import {
  bindPaymentIntentKeyToTransaction,
  paymentAppError,
  paymentErrorMessage,
  paymentIntentKey,
  settlePaymentIntentKey,
  type PaymentIntentKeyInput,
} from '@/lib/paymentSafety';
import {
  Package,
  Coins,
  AlertCircle,
  CheckCircle2,
  Clock,
  BarChart3,
  XCircle,
  AlertTriangle,
  Shield,
  Zap,
  Sparkles,
  ChevronDown,
  Check,
  ShoppingCart,
  Loader2,
  Minus,
  Plus,
  Trash2,
} from 'lucide-react';
import { InvoiceViewerDialog } from '@/components/billing/InvoiceViewerDialog';
import { BillingHistoryCard } from '@/components/billing/BillingHistoryCard';
import { TrialBanner } from '@/components/trial/TrialBanner';
import { useTrialStatus, hasTrialExtras } from '@/hooks/useTrialStatus';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SarSymbol from '@/components/SarSymbol';
import { useEntitlement, getLimitTypeLabel } from '@/hooks/useEntitlement';
import { SERVICE_KEYS } from '@/lib/serviceKeys';
import { toast } from 'sonner';
import { formatLocalizedDate } from '@/lib/localizedDate';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { PaymentReturnBanner } from '@/components/billing/PaymentReturnBanner';
import { BillingGate } from '@/components/billing/BillingGate';
import { useBillingReadiness } from '@/hooks/useBillingReadiness';
import { PlanBundleShowcase } from '@/components/billing/PlanBundleShowcase';
import { getPlanBundleServices, type PlanBundleServiceLike } from '@/lib/billing/planBundleServices';
import { CheckoutPurchaseDialog } from '@/components/billing/CheckoutPurchaseDialog';
import { BillingConfirmationDialog } from '@/components/billing/BillingConfirmationDialog';
import { CreditCartLinePricing, CreditCartPricing, CreditCartPricingStatus, CreditPackGrossPrice, PlanGrossPrice, PlanPricingBreakdown } from '@/components/billing/CreditPricingDisplay';
import { useCreditCartPricing } from '@/hooks/useCreditCartPricing';
import { buildBillingConsentBinding, CONSENT_TEXT_VERSION } from '@/lib/billing/consentPaymentBinding';
import {
  buildCreditPricingBinding,
  buildQuoteBoundCreditPaymentAttempt,
} from '@/lib/billing/creditPricingBinding';
import {
  creditCheckoutResponseError,
  isCreditPricingChangedError,
  recoverCreditPricingChange,
} from '@/lib/billing/creditPricingChangedRecovery';
import type { MoyasarPaymentFormConfig } from '@/components/billing/MoyasarPaymentForm';
import { SecurePaymentDialog } from '@/components/billing/SecurePaymentDialog';
import { ActiveSubscriptionPlanChangeDialog } from '@/components/billing/ActiveSubscriptionPlanChangeDialog';
import { ActiveSubscriptionUpgradeAction } from '@/components/billing/ActiveSubscriptionUpgradeAction';
import { RecurringPaymentMethodDialog } from '@/components/billing/RecurringPaymentMethodDialog';
import { SubscriptionCancellationManagement } from '@/components/billing/SubscriptionCancellationManagement';
import { resolveAuthoritativeCancellationDate } from '@/lib/billing/subscriptionCancellation';
import { buildFunctionUrl, getSupabasePublishableKey } from '@/lib/selfHostedUrls';
import {
  activateMockPaidCheckout,
  canActivateMockPaidCheckout,
  getCheckoutRedirectUrl,
} from '@/lib/billing/paymentCheckout';
import type { ForkHireUniversalWalletRow } from '@/integrations/supabase/creditMarketplaceTypes';
import { useCreditPaymentIntentReconciliation } from '@/hooks/useCreditPaymentIntentReconciliation';
import {
  buildBillingHistoryEntries,
  type BillingHistoryInvoice,
} from '@/lib/billing/billingHistory';
import { fetchCustomerBillingHistory } from '@/lib/billing/customerBillingHistory';
import { usePricingDisplayCatalog } from '@/hooks/usePricingDisplay';
import { planIntroPricing, planPricingForCycle } from '@/lib/billing/pricingDisplay';
import {
  CUSTOMER_ADD_ON_PURCHASE_SELECT,
  CUSTOMER_CREDIT_BALANCE_SELECT,
  CUSTOMER_CREDIT_PACK_SELECT,
  CUSTOMER_HIRE_PLAN_SELECT,
  CUSTOMER_HIRE_WALLET_SELECT,
  CUSTOMER_ORG_SUBSCRIPTION_SELECT,
  CUSTOMER_PLAN_SERVICE_SELECT,
} from '@/lib/billing/customerBillingSelectors';
import type { Tables } from '@/integrations/supabase/types';

const SERVICE_ICONS: Record<string, ElementType> = {
  [SERVICE_KEYS.JOB_MANAGEMENT]: Package,
  [SERVICE_KEYS.AI_CV_SCORING]: Zap,
  [SERVICE_KEYS.AI_INTERVIEWS]: BarChart3,
  [SERVICE_KEYS.TEAM_MANAGEMENT]: Shield,
  [SERVICE_KEYS.AI_JOB_GENERATOR]: Sparkles,
};

interface HirePlan {
  id: string;
  plan_key: string | null;
  plan_name: string;
  description: string | null;
  price_monthly_sar: number | null;
  standard_price_sar: number | null;
  price_yearly_sar: number | null;
  price_annual_sar: number | null;
  intro_price_sar: number | null;
  intro_offer_enabled: boolean | null;
  is_free: boolean | null;
  is_popular: boolean | null;
  display_order: number | null;
}

type CustomerCreditBalance = Pick<
  Tables<'credit_balances'>,
  'id' | 'credit_type' | 'balance' | 'total_purchased' | 'total_consumed'
>;

type CustomerCreditPack = Pick<
  Tables<'credit_pack_products'>,
  'id' | 'name' | 'credit_type' | 'credits_included' | 'price_sar' | 'valid_days'
>;

type CustomerAddOnPurchase = Pick<
  Tables<'add_on_purchases'>,
  'id' | 'service_key' | 'units_purchased' | 'units_remaining'
> & {
  add_ons: Pick<Tables<'add_ons'>, 'name'> | null;
};

interface CreditCartItemCandidate {
  pack: CustomerCreditPack | undefined;
  quantity: number;
}

interface CreditCartItem {
  pack: CustomerCreditPack;
  quantity: number;
}

function isCreditCartItem(item: CreditCartItemCandidate): item is CreditCartItem {
  return item.pack !== undefined && item.quantity > 0;
}

type BillingPurchaseState = 'select' | 'payment' | 'processing';

function BillingSectionError({
  title,
  message,
  onRetry,
  retryLabel,
}: {
  title: string;
  message: string;
  onRetry: () => void;
  retryLabel: string;
}) {
  return (
    <Alert variant="destructive" className="border-destructive/40">
      <AlertCircle className="h-4 w-4" />
      <AlertTitle>{title}</AlertTitle>
      <AlertDescription className="space-y-3">
        <p>{message}</p>
        <Button type="button" size="sm" variant="outline" onClick={onRetry}>
          {retryLabel}
        </Button>
      </AlertDescription>
    </Alert>
  );
}

function HireUsageServiceCard({
  icon: Icon,
  label,
  used,
  limit,
  isUnlimited,
  creditBalance,
  limitTypeLabel,
  trialExtra,
  unlimitedLabel,
  trialBonusLabel,
}: {
  icon: ElementType;
  label: string;
  used: number;
  limit: number;
  isUnlimited: boolean;
  creditBalance: number;
  limitTypeLabel?: string;
  trialExtra?: number;
  unlimitedLabel: string;
  trialBonusLabel?: string;
}) {
  const pct = isUnlimited || limit <= 0 ? 0 : Math.min(Math.round((used / limit) * 100), 100);
  const isOver = !isUnlimited && limit > 0 && used >= limit;
  const isNear = !isUnlimited && limit > 0 && pct >= 80;

  return (
    <div className="space-y-2 rounded-lg border border-border bg-background p-3">
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10">
            <Icon className="h-4 w-4 text-primary" />
          </div>
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-foreground">{label}</p>
            {!isUnlimited && limitTypeLabel && (
              <p className="text-[11px] text-muted-foreground">{limitTypeLabel}</p>
            )}
          </div>
        </div>
        <span className="shrink-0 font-mono text-sm text-foreground">
          {used} / {isUnlimited ? '∞' : limit}
          {creditBalance > 0 && <span className="ms-1 text-primary">+{creditBalance}</span>}
        </span>
      </div>
      {!isUnlimited && limit > 0 && (
        <Progress
          value={pct}
          className={`h-1.5 ${isOver ? '[&>div]:bg-destructive' : isNear ? '[&>div]:bg-amber-500' : '[&>div]:bg-primary'}`}
        />
      )}
      <div className="flex flex-wrap items-center gap-2 text-xs">
        {isUnlimited && (
          <span className="inline-flex items-center gap-1 text-primary">
            <Sparkles className="h-3 w-3" />{unlimitedLabel}
          </span>
        )}
        {(trialExtra ?? 0) > 0 && trialBonusLabel && (
          <span className="rounded-full bg-emerald-500/10 px-2 py-0.5 text-emerald-700 dark:text-emerald-300">
            {trialBonusLabel}
          </span>
        )}
      </div>
    </div>
  );
}

export default function BillingSettings() {
  const { t, i18n } = useTranslation();
  const { profile, user } = useAuth();
  const { data: pricingCatalog, isLoading: pricingCatalogLoading } = usePricingDisplayCatalog(supabase, 'forkhire', !!user);
  const orgId = profile?.org_id;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedInvoice, setSelectedInvoice] = useState<BillingHistoryInvoice | null>(null);
  const [selectedPurchasePlan, setSelectedPurchasePlan] = useState<HirePlan | null>(null);
  const [selectedPlanChangeTarget, setSelectedPlanChangeTarget] = useState<HirePlan | null>(null);
  const [billingConsent, setBillingConsent] = useState(false);
  const [purchaseDialogOpen, setPurchaseDialogOpen] = useState(false);
  const [purchaseBillingDialogOpen, setPurchaseBillingDialogOpen] = useState(false);
  const [purchaseState, setPurchaseState] = useState<BillingPurchaseState>('select');
  const [paymentForm, setPaymentForm] = useState<MoyasarPaymentFormConfig | null>(null);
  const [creditCart, setCreditCart] = useState<Record<string, number>>({});
  const [creditBillingDialogOpen, setCreditBillingDialogOpen] = useState(false);
  const [creditConfirmationRevision, setCreditConfirmationRevision] = useState(0);
  const [creditPaymentForm, setCreditPaymentForm] = useState<MoyasarPaymentFormConfig | null>(null);
  const creditCartRef = useRef<HTMLDivElement | null>(null);
  const { data: billingReadiness, isLoading: readinessLoading } = useBillingReadiness(orgId, {
    partyRole: 'buyer',
    strictB2B: true,
    platform: 'forkhire',
  });

  const jobEnt = useEntitlement(SERVICE_KEYS.JOB_MANAGEMENT);
  const cvEnt = useEntitlement(SERVICE_KEYS.AI_CV_SCORING);
  const interviewEnt = useEntitlement(SERVICE_KEYS.AI_INTERVIEWS);
  const teamEnt = useEntitlement(SERVICE_KEYS.TEAM_MANAGEMENT);
  const { data: trialStatus } = useTrialStatus();

  const {
    data: allHirePlans = [],
    isLoading: hirePlansLoading,
    isError: hirePlansError,
    refetch: refetchHirePlans,
  } = useQuery<HirePlan[]>({
    queryKey: ['hire-plans'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('plans')
        .select(CUSTOMER_HIRE_PLAN_SELECT)
        .eq('platform', 'forkhire')
        .eq('is_active', true)
        .order('display_order');
      if (error) throw error;
      return (data ?? []) as HirePlan[];
    },
  });
  const hirePlanIds = allHirePlans.map((p) => p.id);
  const {
    data: hirePlanServicesData = [],
    isLoading: hirePlanServicesLoading,
  } = useQuery<PlanBundleServiceLike[]>({
    queryKey: ['hire-billing-plan-services', hirePlanIds],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('plan_services')
        .select(CUSTOMER_PLAN_SERVICE_SELECT)
        .in('plan_id', hirePlanIds)
        .eq('is_enabled', true)
        .order('usage_limit', { ascending: false });
      if (error) throw error;
      return (data ?? []) as PlanBundleServiceLike[];
    },
    enabled: hirePlanIds.length > 0,
  });

  const {
    data: subscription,
    isLoading: subLoading,
    isError: subError,
    error: subscriptionError,
    refetch: refetchSubscription,
  } = useQuery({
    queryKey: ['org-subscription', orgId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('organization_subscriptions')
        .select(CUSTOMER_ORG_SUBSCRIPTION_SELECT)
        .eq('org_id', orgId!)
        .in('status', ['active', 'payment_failed', 'expired'])
        .order('starts_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!orgId,
  });

  const { data: pendingUpgradePayment } = useQuery<{ id: string } | null>({
    queryKey: ['org-pending-plan-change', orgId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('payment_transactions')
        .select('id')
        .eq('owner_type', 'organization')
        .eq('owner_id', orgId!)
        .eq('payment_type', 'upgrade')
        .eq('status', 'pending')
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!orgId,
  });

  const {
    data: billingHistory = { payments: [], invoices: [] },
    isLoading: billingHistoryLoading,
    isError: billingHistoryError,
    error: billingHistoryQueryError,
    refetch: refetchBillingHistory,
  } = useQuery({
    queryKey: ['org-customer-billing-history-recent', orgId],
    queryFn: () => fetchCustomerBillingHistory(supabase, {
      ownerType: 'organization',
      ownerId: orgId!,
      limit: 3,
    }),
    enabled: !!orgId,
  });
  const { payments: billingTransactions, invoices: billingInvoices } = billingHistory;

  const {
    data: credits = [],
    isLoading: credLoading,
    isError: credError,
    error: creditsError,
    refetch: refetchCredits,
  } = useQuery<CustomerCreditBalance[]>({
    queryKey: ['org-credits', orgId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('credit_balances')
        .select(CUSTOMER_CREDIT_BALANCE_SELECT)
        .eq('owner_type', 'organization')
        .eq('owner_id', orgId!);
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!orgId,
  });

  const {
    data: universalWalletRead = { wallet: null, error: null },
    isLoading: universalWalletLoading,
    refetch: refetchUniversalWallet,
  } = useQuery<{
    wallet: Pick<ForkHireUniversalWalletRow, 'balance' | 'total_purchased' | 'total_consumed'> | null;
    error: string | null;
  }>({
    queryKey: ['forkhire-universal-wallet', orgId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('forkhire_credit_wallets')
        .select(CUSTOMER_HIRE_WALLET_SELECT)
        .eq('org_id', orgId!)
        .maybeSingle();

      if (error) return { wallet: null, error: error.message };
      return {
        wallet: (data ?? null) as Pick<ForkHireUniversalWalletRow, 'balance' | 'total_purchased' | 'total_consumed'> | null,
        error: null,
      };
    },
    enabled: !!orgId,
  });

  const {
    data: addOns = [],
    isLoading: addOnsLoading,
    isError: addOnsError,
    error: addOnsQueryError,
    refetch: refetchAddOns,
  } = useQuery<CustomerAddOnPurchase[]>({
    queryKey: ['org-addons', orgId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('add_on_purchases')
        .select(CUSTOMER_ADD_ON_PURCHASE_SELECT)
        .eq('org_id', orgId!)
        .eq('status', 'active');
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!orgId,
  });

  const {
    data: creditPacks = [],
    isLoading: creditPacksLoading,
    error: creditPacksError,
    refetch: refetchCreditPacks,
  } = useQuery<CustomerCreditPack[]>({
    queryKey: ['credit-packs-forkhire'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('credit_pack_products')
        .select(CUSTOMER_CREDIT_PACK_SELECT)
        .eq('platform', 'forkhire')
        .eq('is_active', true)
        .is('archived_at', null)
        .order('price_sar', { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
  });

  const cancelMutation = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke('cancel-subscription', {
        body: { subscription_id: subscription?.id, owner_type: 'organization' },
      });
      if (error) throw error;
      if (data?.success === false) throw new Error(data.error || t('hire.billingSettings.errors.cancellationFailed'));
      return data;
    },
    onSuccess: (data) => {
      toast.success(t('hire.billingSettings.toasts.cancellationConfirmed', {
        date: data.effective_at ? formatLocalizedDate(data.effective_at, 'MMM dd, yyyy', i18n.language) : t('hire.billingSettings.endOfPeriod'),
      }));
      queryClient.invalidateQueries({ queryKey: ['org-subscription'] });
    },
    onError: (err: Error) => {
      showAppError(err, { product: 'forkhire', domain: 'billing' });
    },
  });

  const revokeCancelMutation = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke('cancel-subscription', {
        body: { subscription_id: subscription?.id, owner_type: 'organization', action: 'revoke_cancel' },
      });
      if (error) throw error;
      if (data?.success === false) throw new Error(data.user_message || data.error || t('hire.billingSettings.errors.revokeFailed'));
      return data;
    },
    onSuccess: () => {
      toast.success(t('hire.billingSettings.toasts.cancellationRevoked'));
      queryClient.invalidateQueries({ queryKey: ['org-subscription'] });
    },
    onError: (err: Error) => {
      showAppError(err, { product: 'forkhire', domain: 'billing' });
    },
  });

  const plan = subscription?.plans as HirePlan | undefined;
  if (subError && subscriptionError) console.error('Billing subscription query failed:', subscriptionError);
  if (billingHistoryError && billingHistoryQueryError) console.error('Billing history query failed:', billingHistoryQueryError);
  if (credError && creditsError) console.error('Billing credits query failed:', creditsError);
  if (addOnsError && addOnsQueryError) console.error('Billing add-ons query failed:', addOnsQueryError);

  const isLoading = subLoading;
  const isCancellationPending = !!subscription?.cancellation_requested_at;
  const isGracePeriod = subscription?.status === 'payment_failed';
  const isExpired = subscription?.status === 'expired';
  const trialExtraAllowances = hasTrialExtras(trialStatus) ? (trialStatus?.trial_extra_allowances ?? {}) : {};
  const formatBillingDate = (date?: string | null, fallback = '-') =>
    date ? formatLocalizedDate(date, 'MMM dd, yyyy', i18n.language) : fallback;
  const billingStatusLabel = subscription?.status === 'payment_failed'
    ? t('hire.billingSettings.billingStatus.paymentAttentionNeeded')
    : subscription?.status === 'expired'
      ? t('hire.billingSettings.billingStatus.expired')
      : subscription?.status === 'active'
        ? t('hire.billingSettings.billingStatus.active')
        : subscription?.status ? subscription.status.replace(/_/g, ' ') : t('common.notAvailable');
  const billingLifecycleLabel = isCancellationPending
    ? t('hire.billingSettings.lifecycle.cancelsAtPeriodEnd')
    : isExpired
      ? t('hire.billingSettings.lifecycle.accessExpired')
      : subscription?.renewal_date
        ? t('hire.billingSettings.lifecycle.renewsAutomatically')
        : t('hire.billingSettings.lifecycle.renewalDateNotAvailable');
  const cancellationEffectiveDate = resolveAuthoritativeCancellationDate(subscription);
  const nextBillingDate = isCancellationPending
    ? cancellationEffectiveDate
    : subscription?.renewal_date || subscription?.expires_at || null;

  const entitlements = [
    { key: SERVICE_KEYS.JOB_MANAGEMENT, ent: jobEnt, label: t('hire.billingSettings.entitlements.activeJobs') },
    { key: SERVICE_KEYS.AI_CV_SCORING, ent: cvEnt, label: t('hire.billingSettings.entitlements.aiCvScoring') },
    { key: SERVICE_KEYS.AI_INTERVIEWS, ent: interviewEnt, label: t('hire.billingSettings.entitlements.aiInterviews') },
    { key: SERVICE_KEYS.TEAM_MANAGEMENT, ent: teamEnt, label: t('hire.billingSettings.entitlements.teamSeats') },
  ];
  const adaptivePlanGridStyle = { gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 22rem), 1fr))' };
  const activeBillingCycle = subscription?.billing_cycle === 'yearly' ? 'yearly' : 'monthly';
  const purchaseBillingCycle = subscription?.status === 'active' ? activeBillingCycle : 'monthly';
  useCreditPaymentIntentReconciliation({
    client: supabase,
    ownerType: 'organization',
    ownerId: orgId,
  });
  const getHirePlanPrice = (p: HirePlan, billingCycle: 'monthly' | 'yearly' = 'monthly') => {
    if (billingCycle === 'yearly') {
      const yearly = Number(p.price_yearly_sar);
      const annual = Number(p.price_annual_sar);
      if (Number.isFinite(yearly) && yearly > 0) return yearly;
      if (Number.isFinite(annual) && annual > 0) return annual;
      return null;
    }
    const standard = Number(p.standard_price_sar);
    const monthly = Number(p.price_monthly_sar);
    if (Number.isFinite(standard) && standard > 0) return standard;
    if (Number.isFinite(monthly) && monthly > 0) return monthly;
    return null;
  };
  const getHireIntroPrice = (p: HirePlan) => {
    const intro = Number(p.intro_price_sar);
    return p.intro_offer_enabled === true && Number.isFinite(intro) && intro > 0 ? intro : null;
  };
  const hirePlanServicesMap: Record<string, PlanBundleServiceLike[]> = {};
  hirePlanServicesData.forEach((ps) => {
    if (!ps.plan_id) return;
    if (!hirePlanServicesMap[ps.plan_id]) hirePlanServicesMap[ps.plan_id] = [];
    hirePlanServicesMap[ps.plan_id].push(ps);
  });
  const visibleHirePlans = allHirePlans
    .slice()
    .sort((a, b) => {
      if (a.is_free && !b.is_free) return -1;
      if (!a.is_free && b.is_free) return 1;
      return (getHirePlanPrice(a, purchaseBillingCycle) ?? Number.MAX_SAFE_INTEGER) -
        (getHirePlanPrice(b, purchaseBillingCycle) ?? Number.MAX_SAFE_INTEGER);
    });

  const getCreditPackId = (pack: Pick<CustomerCreditPack, 'id'>) => pack.id;
  const findCreditPackById = (packId: string) =>
    creditPacks.find((pack) => getCreditPackId(pack) === packId);
  const creditCartItems = Object.entries(creditCart)
    .map(([packId, quantity]) => ({
      pack: findCreditPackById(packId),
      quantity,
    }))
    .filter(isCreditCartItem);
  const creditCartPricingItems = creditCartItems.map((item) => ({
    packId: String(item.pack.id),
    quantity: item.quantity,
  }));
  const creditCartPricing = useCreditCartPricing('forkhire', creditCartPricingItems);
  const creditCartTotal = creditCartItems.reduce(
    (sum, item) => sum + Number(item.pack.price_sar) * item.quantity,
    0,
  );
  const addCreditPackToCart = (packId: unknown, packName?: string | null) => {
    const normalizedPackId = String(packId ?? '');
    if (!normalizedPackId) return;
    setCreditCart((prev) => ({
      ...prev,
      [normalizedPackId]: (prev[normalizedPackId] ?? 0) + 1,
    }));
    toast.success(t('hire.billingCredits.cart.addedToCart'), {
      description: packName ?? t('hire.billingCredits.cart.title'),
    });
    window.requestAnimationFrame(() => {
      creditCartRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  };
  const setCreditPackQuantity = (packId: unknown, quantity: number) => setCreditCart((prev) => {
    const normalizedPackId = String(packId ?? '');
    if (!normalizedPackId) return prev;
    const next = { ...prev };
    if (quantity <= 0) delete next[normalizedPackId];
    else next[normalizedPackId] = quantity;
    return next;
  });
  const clearCreditCart = () => setCreditCart({});
  const openCreditCheckout = () => {
    if (creditCartItems.length === 0) {
      toast.error(t('hire.billingCredits.errors.cartEmpty'));
      return;
    }
    if (!orgId) {
      toast.error(t('hire.checkout.missingPlanOrOrg'));
      return;
    }
    setCreditBillingDialogOpen(true);
  };

  const selectedPurchasePrice = selectedPurchasePlan ? getHirePlanPrice(selectedPurchasePlan, purchaseBillingCycle) : null;
  const selectedPurchaseIntro = selectedPurchasePlan && purchaseBillingCycle === 'monthly'
    ? getHireIntroPrice(selectedPurchasePlan)
    : null;
  const selectedPurchasePriceAvailable = selectedPurchasePrice != null && selectedPurchasePrice > 0
    && Boolean(planPricingForCycle(pricingCatalog, selectedPurchasePlan?.id, purchaseBillingCycle));
  const selectedPurchaseRenewalPricing = planPricingForCycle(
    pricingCatalog,
    selectedPurchasePlan?.id,
    purchaseBillingCycle,
  );
  const selectedPurchaseIntroPricing = selectedPurchaseIntro != null
    ? planIntroPricing(pricingCatalog, selectedPurchasePlan?.id)
    : null;
  const selectedPurchaseDuePricing = selectedPurchaseIntro != null
    ? selectedPurchaseIntroPricing
    : selectedPurchaseRenewalPricing;
  const selectedPurchasePricingReady = Boolean(
    selectedPurchaseDuePricing &&
      selectedPurchaseRenewalPricing &&
      !pricingCatalogLoading,
  );
  const purchaseMutation = useMutation({
    mutationFn: async () => {
      if (!selectedPurchasePlan || !orgId) throw new Error(t('hire.checkout.missingPlanOrOrg'));
      setPurchaseState('processing');

      const renewalPrice = getHirePlanPrice(selectedPurchasePlan, purchaseBillingCycle);
      const introPrice = purchaseBillingCycle === 'monthly' ? getHireIntroPrice(selectedPurchasePlan) : null;
      const amount = introPrice != null ? introPrice : renewalPrice;
      const renewalPricing = planPricingForCycle(pricingCatalog, selectedPurchasePlan.id, purchaseBillingCycle);
      const introPricing = purchaseBillingCycle === 'monthly'
        ? planIntroPricing(pricingCatalog, selectedPurchasePlan.id)
        : null;

      if (!renewalPrice || !amount || !renewalPricing) throw new Error(t('hire.checkout.planPricingNotConfigured'));

      const consentBinding = buildBillingConsentBinding({
        planId: selectedPurchasePlan.id,
        billingCycle: purchaseBillingCycle,
        taxConfigId: pricingCatalog?.tax_config_id,
        duePricing: introPricing ?? renewalPricing,
        introPricing,
        renewalPricing,
        consentLanguage: i18n.resolvedLanguage ?? i18n.language,
      });
      if (!consentBinding) throw new Error(t('pricingDisplay.unavailable'));

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error(t('hire.checkout.notAuthenticated'));
      const attempt: PaymentIntentKeyInput = {
        ownerType: 'organization',
        ownerId: orgId,
        paymentType: 'subscription',
        productKey: selectedPurchasePlan.id,
        billingCycle: purchaseBillingCycle,
      };
      let trialExtras: { trial_extra_days?: number; trial_extra_allowances?: Record<string, number> } = {};
      try {
        const { data: trialResult } = await supabase.functions.invoke('manage-trial', {
          body: {
            action: 'subscribe_during_trial',
            platform: 'forkhire',
            owner_type: 'organization',
            owner_id: orgId,
            plan_id: selectedPurchasePlan.id,
          },
        });
        if (trialResult?.success) {
          trialExtras = {
            trial_extra_days: trialResult.trial_extra_days ?? 0,
            trial_extra_allowances: trialResult.trial_extra_allowances ?? {},
          };
        }
      } catch (trialErr) {
        console.warn('[BillingSettings] Trial integration skipped:', trialErr);
      }

      const res = await fetch(buildFunctionUrl('create-payment'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
          apikey: getSupabasePublishableKey(),
        },
        body: JSON.stringify({
          owner_type: 'organization',
          owner_id: orgId,
          payment_type: 'subscription',
          idempotency_key: paymentIntentKey(attempt),
          amount_sar: amount,
          description: `${selectedPurchasePlan.plan_name} - ${purchaseBillingCycle}`,
          callback_url: `${window.location.origin}/settings/billing`,
          billing_consent: consentBinding,
          metadata: {
            plan_id: selectedPurchasePlan.id,
            billing_cycle: purchaseBillingCycle,
            billing_consent_version: CONSENT_TEXT_VERSION,
            billing_consent_accepted: 'true',
            billing_confirmation_confirmed: 'true',
            trial_extra_days: String(trialExtras.trial_extra_days ?? 0),
          },
        }),
      });

      if (!res.ok) {
        const err = await res.json();
        throw paymentAppError(
          err,
          i18n.language,
          t('hire.checkout.paymentFailed'),
          t('hire.checkout.paymentCouldNotComplete'),
        );
      }
      const data = await res.json();
      bindPaymentIntentKeyToTransaction(attempt, data.transaction_id);
      settlePaymentIntentKey(attempt, data.status);
      return data;
    },
    onSuccess: async (data) => {
      try {
        if (data.status === 'downgrade_scheduled') {
          setPurchaseDialogOpen(false);
          setPurchaseBillingDialogOpen(false);
          setPurchaseState('select');
          await queryClient.invalidateQueries({ queryKey: ['org-subscription'] });
          toast.success(t('hire.planChange.downgradeScheduledTitle'), {
            description: paymentErrorMessage(data, i18n.language, t('hire.planChange.downgradeScheduledTitle')),
          });
          return;
        }
        if (data.payment_form) {
          setPurchaseDialogOpen(false);
          setPurchaseBillingDialogOpen(false);
          setPaymentForm(data.payment_form);
          setPurchaseState('payment');
          return;
        }
        const redirectUrl = getCheckoutRedirectUrl(data);
        if (redirectUrl) {
          window.location.href = redirectUrl;
        } else if (canActivateMockPaidCheckout(data)) {
          await activateMockPaidCheckout(supabase, data);
          setPurchaseDialogOpen(false);
          setPurchaseBillingDialogOpen(false);
          setPurchaseState('select');
          queryClient.invalidateQueries({ queryKey: ['org-subscription'] });
          queryClient.invalidateQueries({ queryKey: ['org-billing-history-transactions-recent'] });
          queryClient.invalidateQueries({ queryKey: ['org-billing-history-invoices-recent'] });
          toast.success(t('hire.checkout.localMockPaymentActivated'));
        } else {
          throw new Error(t('hire.checkout.checkoutUnavailable'));
        }
      } catch (err) {
        setPurchaseState('select');
        showAppError(err as Error, { product: 'forkhire', domain: 'billing' });
      }
    },
    onError: (err: unknown) => {
      setPurchaseState('select');
      showAppError(err, { product: 'forkhire', domain: 'billing' });
    },
  });
  const creditPurchaseMutation = useMutation({
    retry: false,
    mutationFn: async () => {
      if (!creditCartPricing.ready) throw new Error(t('pricingDisplay.unavailable'));
      const cartItems = Object.entries(creditCart).map(([packId, quantity]) => ({ packId, quantity }));
      if (cartItems.length === 0) throw new Error(t('hire.billingCredits.errors.cartEmpty'));
      const creditPricingBinding = buildCreditPricingBinding(cartItems, creditCartPricing.quote);
      if (!creditPricingBinding) throw new Error(t('pricingDisplay.unavailable'));

      const selectedPacks = cartItems.map((item) => ({
        pack: findCreditPackById(item.packId),
        quantity: item.quantity,
      }));
      const completeSelectedPacks = selectedPacks.filter(isCreditCartItem);
      if (completeSelectedPacks.length !== selectedPacks.length) throw new Error(t('hire.billingCredits.errors.packNotFound'));

      const amount = completeSelectedPacks.reduce(
        (sum, item) => sum + Number(item.pack.price_sar) * item.quantity,
        0,
      );

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error(t('hire.checkout.notAuthenticated'));
      if (!orgId) throw new Error(t('hire.checkout.missingPlanOrOrg'));
      const attempt = buildQuoteBoundCreditPaymentAttempt({
        ownerType: 'organization',
        ownerId: orgId,
        items: cartItems,
        pricingBinding: creditPricingBinding,
      });

      const res = await fetch(buildFunctionUrl('create-payment'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
          apikey: getSupabasePublishableKey(),
        },
        body: JSON.stringify({
          owner_type: 'organization',
          owner_id: orgId,
          payment_type: 'credit_pack',
          idempotency_key: paymentIntentKey(attempt),
          amount_sar: amount,
          description: t('hire.billingCredits.cart.paymentDescription', { count: cartItems.length }),
          callback_url: `${window.location.origin}/settings/billing`,
          credit_pricing_binding: creditPricingBinding,
          metadata: {
            billing_confirmation_confirmed: 'true',
            credit_cart_json: JSON.stringify(
              cartItems.map((item) => ({
                credit_pack_product_id: item.packId,
                quantity: item.quantity,
              })),
            ),
          },
        }),
      });

      if (!res.ok) {
        const err = await res.json();
        throw creditCheckoutResponseError({
          httpStatus: res.status,
          payload: err,
          language: i18n.language,
          fallback: paymentErrorMessage(err, i18n.language, t('hire.billingCredits.errors.paymentFailed')),
        });
      }
      const data = await res.json();
      bindPaymentIntentKeyToTransaction(attempt, data.transaction_id);
      settlePaymentIntentKey(attempt, data.status);
      return { data, attempt };
    },
    onSuccess: async ({ data, attempt }) => {
      try {
        if (data.payment_form) {
          setCreditBillingDialogOpen(false);
          setCreditPaymentForm(data.payment_form);
          return;
        }
        const redirectUrl = getCheckoutRedirectUrl(data);
        if (redirectUrl) {
          window.location.href = redirectUrl;
          return;
        }
        if (!canActivateMockPaidCheckout(data)) {
          throw new Error(t('hire.billingCredits.errors.checkoutUnavailable'));
        }

        await activateMockPaidCheckout(supabase, data);
        settlePaymentIntentKey(attempt, 'paid_and_activated');
        setCreditBillingDialogOpen(false);
        toast.success(t('hire.billingCredits.localMockPaymentActivatedTitle'), {
          description: t('hire.billingCredits.localMockPaymentActivatedDescription'),
        });
        setCreditCart({});
        queryClient.invalidateQueries({ queryKey: ['org-credits'] });
        queryClient.invalidateQueries({ queryKey: ['forkhire-universal-wallet'] });
        queryClient.invalidateQueries({ queryKey: ['forkhire-universal-credit-ledger'] });
        queryClient.invalidateQueries({ queryKey: ['forkhire-credit-purchase-attempts'] });
      } catch (err) {
        showAppError(err as Error, { product: 'forkhire', domain: 'billing' });
      }
    },
    onError: async (err) => {
      if (isCreditPricingChangedError(err)) {
        await recoverCreditPricingChange({
          closeConfirmation: () => setCreditBillingDialogOpen(false),
          clearPaymentForm: () => setCreditPaymentForm(null),
          resetConfirmation: () => setCreditConfirmationRevision((revision) => revision + 1),
          invalidateQuote: () => queryClient.invalidateQueries({
            queryKey: ['pricing-display-quote', 'forkhire'],
            refetchType: 'none',
          }),
          refetchQuote: () => creditCartPricing.refetch(),
          notifyCustomer: () => toast.error(t('pricingDisplay.creditPriceChanged')),
        });
        return;
      }
      showAppError(err, { product: 'forkhire', domain: 'billing' });
    },
  });
  const creditFlowLocked = creditBillingDialogOpen || creditPurchaseMutation.isPending;
  const creditCheckoutLocked = creditFlowLocked || !creditCartPricing.ready;
  const purchaseFlowLocked = purchaseMutation.isPending || purchaseState === 'processing';
  const planChangeInProgress = Boolean(
    selectedPlanChangeTarget ||
    subscription?.downgrade_plan_id ||
    pendingUpgradePayment,
  );
  const upgradeCheckoutBlockedReason =
    !readinessLoading && billingReadiness?.ready === false
      ? t('hire.planChange.completeBillingIdentityBeforeUpgrade')
      : null;
  const openPlanPurchase = (targetPlan?: HirePlan | null) => {
    if (
      !targetPlan ||
      targetPlan.is_free ||
      !getHirePlanPrice(targetPlan, purchaseBillingCycle) ||
      !planPricingForCycle(pricingCatalog, targetPlan.id, purchaseBillingCycle)
    ) return;
    if (shouldUsePaidCyclePlanChange(subscription?.status, plan?.is_free)) {
      setSelectedPlanChangeTarget(targetPlan);
      return;
    }
    setSelectedPurchasePlan(targetPlan);
    setBillingConsent(false);
    setPurchaseBillingDialogOpen(false);
    setPurchaseDialogOpen(true);
  };

  const legacyCreditsTotal = credits.reduce((sum, credit) => sum + Number(credit.balance ?? 0), 0);
  const walletCreditsTotal = Number(universalWalletRead.wallet?.balance ?? 0);
  const hasUniversalWallet = Boolean(universalWalletRead.wallet);
  const totalCredits = hasUniversalWallet ? walletCreditsTotal : legacyCreditsTotal;
  const creditsSummaryLoading = credLoading || universalWalletLoading;
  const creditsSummaryError = !hasUniversalWallet && credError;

  const statusBadge = (status: string) => {
    if (status === 'active') return <Badge className="bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300 border-0 text-xs gap-1"><CheckCircle2 className="w-3 h-3" />{t('hire.billingSettings.statuses.active')}</Badge>;
    if (status === 'paid') return <Badge className="bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300 border-0 text-xs">{t('hire.billingSettings.statuses.paid')}</Badge>;
    if (status === 'pending') return <Badge variant="secondary" className="text-xs gap-1"><Clock className="w-3 h-3" />{t('hire.billingSettings.statuses.pending')}</Badge>;
    if (status === 'failed' || status === 'payment_failed') return <Badge variant="destructive" className="text-xs gap-1"><XCircle className="w-3 h-3" />{t('hire.billingSettings.statuses.failed')}</Badge>;
    if (status === 'expired') return <Badge variant="outline" className="text-muted-foreground text-xs gap-1"><XCircle className="w-3 h-3" />{t('hire.billingSettings.statuses.expired')}</Badge>;
    return <Badge variant="outline" className="text-xs">{status}</Badge>;
  };

  const billingHistoryEntries = useMemo(
    () => buildBillingHistoryEntries(billingTransactions, billingInvoices, {
      limit: 3,
      ownerType: 'organization',
      ownerId: orgId,
    }),
    [billingTransactions, billingInvoices, orgId],
  );

  return (
    <AppLayout>
      <div className={`${HIRE_SECTION_SPACING} ${HIRE_PAGE_CONTAINER}`}>
        <PaymentReturnBanner invalidateKeys={[['org-subscription'], ['plan-usage'], ['entitlement']]} />
        <BillingGate readiness={billingReadiness} isLoading={readinessLoading} remediationPath="/settings/billing/identity" />

        <Card className="shadow-card border-border border-t-4 border-t-primary overflow-hidden">
          <div className="p-3.5 sm:p-5 lg:p-8">
            <h1 className="text-base sm:text-2xl lg:text-3xl font-bold font-display text-foreground">
              {t('hire.billingSettings.title')}
            </h1>
            <p className="text-muted-foreground mt-0.5 sm:mt-1.5 text-xs sm:text-sm">
              {t('hire.billingSettings.subtitle')}
            </p>
          </div>
        </Card>

        <TrialBanner />

        <Card className="shadow-card border-border rounded-2xl overflow-hidden">
          <div className="p-5 sm:p-6 pb-4">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <h2 className="text-base font-bold font-display text-foreground flex items-center gap-2">
                  <Package className="w-5 h-5 text-primary" />
                  {t('hire.billingSettings.availablePlans')}
                </h2>
                <p className="mt-1 text-sm text-muted-foreground">{t('hire.billingSettings.availablePlansDesc')}</p>
              </div>
              <Badge variant="outline" className="w-fit border-primary/30 text-primary">
                {t('hire.billingSettings.plansVisibleUpfront')}
              </Badge>
            </div>
          </div>
          <div className="px-5 sm:px-6 pb-5 sm:pb-6">
            {hirePlansLoading || hirePlanServicesLoading ? (
              <div className="grid gap-4" style={adaptivePlanGridStyle}>
                {[1, 2, 3].map((i) => <Skeleton key={i} className="h-80 rounded-xl" />)}
              </div>
            ) : hirePlansError ? (
              <BillingSectionError
                title={t('hire.billingSettings.plansLoadTitle')}
                message={t('hire.billingSettings.plansLoadMessage')}
                onRetry={() => refetchHirePlans()}
                retryLabel={t('common.retry')}
              />
            ) : visibleHirePlans.length === 0 ? (
              <p className="py-8 text-center text-sm text-muted-foreground">{t('hire.billingSettings.noPlansAvailable')}</p>
            ) : (
              <div className="grid gap-4 items-start" style={adaptivePlanGridStyle}>
                {visibleHirePlans.map((p) => {
                  const price = getHirePlanPrice(p, purchaseBillingCycle);
                  const intro = purchaseBillingCycle === 'monthly' ? getHireIntroPrice(p) : null;
                  const isCurrent = plan?.id === p.id;
                  const priceAvailable = p.is_free || (
                    price != null &&
                    Boolean(planPricingForCycle(pricingCatalog, p.id, purchaseBillingCycle))
                  );
                  const services = getPlanBundleServices(p, hirePlanServicesMap[p.id] ?? [], 'forkhire');

                  return (
                    <div
                      key={p.id}
                      className={`relative flex h-full min-w-0 flex-col rounded-lg border border-border bg-background p-4 transition-all ${
                        isCurrent ? 'ring-2 ring-primary shadow-md' : 'hover:shadow-md'
                      } ${p.is_popular && !isCurrent ? 'shadow-md ring-2 ring-warning/80' : ''}`}
                    >
                      {p.is_popular && (
                        <div className="absolute -top-3 start-1/2 z-10 -translate-x-1/2 rtl:translate-x-1/2">
                          <Badge className="bg-warning px-3 py-0.5 text-[10px] text-warning-foreground shadow-sm">
                            {t('hire.checkout.mostPopular')}
                          </Badge>
                        </div>
                      )}
                      {isCurrent && (
                        <div className="absolute top-3 end-3">
                          <Badge variant="outline" className="border-primary/30 text-primary">
                            {t('hire.billingSettings.currentPlan')}
                          </Badge>
                        </div>
                      )}
                      <div className="pb-3 pt-1">
                        <h3 className="pe-20 text-lg font-bold text-foreground">{p.plan_name}</h3>
                        {p.description && <p className="mt-1 line-clamp-4 text-sm leading-relaxed text-muted-foreground">{p.description}</p>}
                      </div>
                      <div className="flex flex-1 flex-col gap-4">
                        <div>
                          {p.is_free ? (
                            <p className="text-3xl font-bold text-foreground">{t('hire.billingSettings.free')}</p>
                          ) : priceAvailable ? (
                            <>
                              <PlanGrossPrice platform="forkhire" planId={p.id} billingCycle={purchaseBillingCycle} introductory={intro != null && intro !== price} />
                              {intro != null && intro !== price && (
                                <div className="mt-1 text-xs text-muted-foreground">{t('hire.checkout.then')}<PlanGrossPrice platform="forkhire" planId={p.id} billingCycle={purchaseBillingCycle} /></div>
                              )}
                            </>
                          ) : (
                            <div className="space-y-1">
                              <p className="text-lg font-semibold text-foreground">{pricingCatalogLoading ? t('pricingDisplay.loading') : t('hire.checkout.pricingUnavailable')}</p>
                              <p className="text-xs text-muted-foreground">{t('hire.checkout.pricingUnavailableDesc')}</p>
                            </div>
                          )}
                        </div>

                        <PlanBundleShowcase
                          services={services}
                          translationScope="hire.checkout"
                          brand="hire"
                          isSelected={isCurrent}
                        />

                        <Button
                          type="button"
                          className="mt-auto rounded-xl"
                          variant={isCurrent ? 'outline' : 'default'}
                          disabled={isCurrent || p.is_free || !priceAvailable}
                          onClick={() => openPlanPurchase(p)}
                        >
                          {isCurrent
                            ? t('hire.billingSettings.currentPlan')
                            : p.is_free
                              ? t('hire.billingSettings.free')
                              : subscription?.status === 'active'
                                ? t('commercialPlanChange.changePlan')
                                : t('hire.billingSettings.choosePlan')}
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </Card>

        {isLoading ? (
          <div className="space-y-6">
            <Skeleton className="h-48 rounded-xl" />
            <Skeleton className="h-36 rounded-xl" />
          </div>
        ) : (
          <>
            {isGracePeriod && (
              <div className="flex items-start gap-3 p-4 rounded-xl border border-destructive/30 bg-destructive/5">
                <AlertTriangle className="w-5 h-5 text-destructive mt-0.5 shrink-0" />
                <div>
                  <p className="font-semibold text-destructive text-sm">{t('hire.billingSettings.paymentFailedTitle')}</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {t('hire.billingSettings.paymentFailedDescription')}
                    {subscription?.grace_period_ends_at && (
                      <> {t('hire.billingSettings.gracePeriodEnds')} <strong>{formatBillingDate(subscription.grace_period_ends_at)}</strong>.</>
                    )}
                  </p>
                  <Button size="sm" className="mt-3" onClick={() => openPlanPurchase(plan ?? visibleHirePlans.find((candidate) => !candidate.is_free))}>
                    {t('hire.billingSettings.updatePaymentMethod')}
                  </Button>
                </div>
              </div>
            )}

            {isCancellationPending && !isExpired && (
              <div className="flex items-start justify-between gap-3 p-4 rounded-xl border border-amber-500/30 bg-amber-50/50 dark:bg-amber-900/10">
                <div className="flex items-start gap-3">
                  <Clock className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
                  <div>
                    <p className="font-semibold text-amber-800 dark:text-amber-300 text-sm">{t('hire.billingSettings.cancellationScheduledTitle')}</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      {t('hire.billingSettings.cancellationScheduledDescription', { date: formatBillingDate(cancellationEffectiveDate, t('hire.billingSettings.endOfPeriod')) })}
                    </p>
                  </div>
                </div>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      size="sm"
                      variant="outline"
                      className="shrink-0 border-amber-600/40 text-amber-800 hover:bg-amber-100 dark:text-amber-300 dark:hover:bg-amber-900/30"
                      disabled={revokeCancelMutation.isPending}
                    >
                      {revokeCancelMutation.isPending ? t('hire.billingSettings.reverting') : t('hire.billingSettings.keepMySubscription')}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>{t('hire.billingSettings.revokeCancelTitle')}</AlertDialogTitle>
                      <AlertDialogDescription>
                        {t('hire.billingSettings.revokeCancelDescription', { date: formatBillingDate(cancellationEffectiveDate) })}
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>{t('common.close')}</AlertDialogCancel>
                      <AlertDialogAction onClick={() => revokeCancelMutation.mutate()}>
                        {t('hire.billingSettings.yesKeepSubscription')}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            )}

            {subscription?.status === 'active' && plan && !plan.is_free && (
              <SubscriptionCancellationManagement
                portal="forkhire"
                currentPlan={plan.plan_name}
                pending={isCancellationPending}
                authoritativeDate={cancellationEffectiveDate}
                paidThroughDate={subscription.paid_through_date ?? null}
                autoRenewEnabled={subscription.auto_renew !== false}
                downgradeCleared={Boolean(subscription.cancellation_cleared_downgrade_plan_id)}
                formatDate={(value) => formatBillingDate(value)}
                cancelling={cancelMutation.isPending}
                undoing={revokeCancelMutation.isPending}
                onCancel={() => cancelMutation.mutate()}
                onUndo={() => revokeCancelMutation.mutate()}
              />
            )}

            <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
              {(subError || plan) && (
                <Card className="lg:col-span-3 shadow-card border-border rounded-2xl overflow-hidden p-5 sm:p-6">
                  {subError ? (
                    <BillingSectionError
                      title={t('hire.billingSettings.subscriptionLoadTitle')}
                      message={t('hire.billingSettings.subscriptionLoadMessage')}
                      onRetry={() => refetchSubscription()}
                      retryLabel={t('common.retry')}
                    />
                  ) : plan ? (
                    <>
                    <div className="flex items-center gap-2.5 mb-1">
                      <h2 className="text-base sm:text-lg font-bold font-display text-foreground">
                        {t('hire.billingSettings.currentPlanPrefix')} <span className="text-primary">{plan.plan_name}</span> {t('hire.billingSettings.currentPlanSuffix')}
                      </h2>
                      {statusBadge(subscription.status)}
                    </div>
                    <p className="text-xs text-muted-foreground mb-5">
                      {isCancellationPending
                        ? t('hire.billingSettings.endsOn', { date: formatBillingDate(cancellationEffectiveDate, t('hire.billingSettings.endOfPeriod')) })
                        : t('hire.billingSettings.renewsOn', { date: formatBillingDate(subscription?.renewal_date) })
                      }
                      {hasTrialExtras(trialStatus) && (trialStatus?.trial_extra_days ?? 0) > 0 && (
                        <span className="text-emerald-600 dark:text-emerald-400 ms-2">
                          {t('hire.billingSettings.trialBonusDays', { count: trialStatus!.trial_extra_days })}
                        </span>
                      )}
                    </p>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5 rounded-lg border border-border bg-muted/20 p-3 text-sm">
                      <div>
                        <p className="text-xs text-muted-foreground">{t('hire.billingSettings.billingStatusLabel')}</p>
                        <p className="font-semibold text-foreground capitalize">{billingStatusLabel}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">{t('hire.billingSettings.renewalStateLabel')}</p>
                        <p className="font-semibold text-foreground">{billingLifecycleLabel}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">{t('hire.billingSettings.nextRenewalLabel')}</p>
                        <p className="font-semibold text-foreground">{formatBillingDate(nextBillingDate)}</p>
                      </div>
                    </div>

                    <div className={`mb-5 ${entitlements.length > 4 ? 'grid grid-cols-2 gap-x-6 gap-y-2' : 'space-y-2'}`}>
                      {entitlements.map(({ key, ent, label }) => (
                        <div key={key} className="flex items-center gap-2 text-sm">
                          <Check className="w-4 h-4 text-primary shrink-0" />
                          <span className="text-foreground">
                            {ent.isUnlimited ? t('hire.billingSettings.unlimited') : ent.limit} {label}
                            {!ent.isUnlimited && getLimitTypeLabel(ent.limitType) && (
                              <span className="text-muted-foreground"> {getLimitTypeLabel(ent.limitType)}</span>
                            )}
                          </span>
                        </div>
                      ))}
                    </div>

                    <div className="mb-5">
                      <PlanGrossPrice platform="forkhire" planId={plan.id} billingCycle={activeBillingCycle} />
                      {subscription.billing_cycle && (
                        <span className="text-xs text-muted-foreground ms-2 capitalize">{t('hire.billingSettings.billingCycle', { cycle: subscription.billing_cycle })}</span>
                      )}
                    </div>

                    <div className="flex items-center gap-1.5 flex-wrap">
                      <Button
                        variant="outline"
                        size="sm"
                        className="rounded-full text-xs h-7 px-3"
                        onClick={() => document.getElementById('hire-credit-bundles')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                      >
                        <Coins className="w-3 h-3 me-1" />{t('hire.billingSettings.creditsButton')}
                      </Button>
                      <ActiveSubscriptionUpgradeAction
                        activePaid={subscription.status === 'active' && !plan.is_free}
                        currentPlan={plan}
                        plans={allHirePlans}
                        billingCycle={activeBillingCycle}
                        planChangeInProgress={planChangeInProgress}
                        pricingCatalog={pricingCatalog}
                        pricingLoading={pricingCatalogLoading}
                        checkoutBlockedReason={upgradeCheckoutBlockedReason}
                        onSelectPlan={(targetPlan) => setSelectedPlanChangeTarget(targetPlan as HirePlan)}
                      />
                      {subscription.status === 'active' && !plan.is_free && !isCancellationPending && (
                        <RecurringPaymentMethodDialog
                          client={supabase}
                          ownerType="organization"
                          subscriptionId={subscription.id}
                          onAuthorized={() => queryClient.invalidateQueries({ queryKey: ['org-subscription'] })}
                        />
                      )}
                    </div>
                    </>
                  ) : null}
                </Card>
              )}

              <Card id="hire-credit-bundles" className={`${subError || plan ? 'lg:col-span-2' : 'lg:col-span-5'} shadow-card border-border rounded-2xl overflow-hidden p-5 sm:p-6 scroll-mt-24`}>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-base font-bold font-display text-foreground">{t('hire.billingSettings.creditsRemainingTitle')}</h2>
                  <span className="text-2xl font-bold font-display text-foreground">{creditsSummaryError ? '-' : totalCredits}</span>
                </div>

                <Progress value={!creditsSummaryError && totalCredits > 0 ? 100 : 0} className="h-2 mb-5 [&>div]:bg-primary" />

                {creditsSummaryLoading ? (
                  <Skeleton className="h-32 rounded-xl" />
                ) : creditsSummaryError ? (
                  <BillingSectionError
                    title={t('hire.billingSettings.creditsLoadTitle')}
                    message={t('hire.billingSettings.creditsLoadMessage')}
                    onRetry={() => {
                      refetchCredits();
                      refetchUniversalWallet();
                    }}
                    retryLabel={t('common.retry')}
                  />
                ) : (
                  <div className="space-y-4">
                    {hasUniversalWallet && (
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-foreground">{t('hire.billingCredits.organizationWalletTitle')}</p>
                          <p className="text-xs text-muted-foreground">
                            {t('hire.billingSettings.consumedOfPurchased', {
                              used: universalWalletRead.wallet?.total_consumed ?? 0,
                              purchased: universalWalletRead.wallet?.total_purchased ?? 0,
                            })}
                          </p>
                        </div>
                        <span className="text-lg font-semibold text-foreground">{walletCreditsTotal}</span>
                      </div>
                    )}

                    {credits.length > 0 && (
                      credits.map((c) => (
                        <div key={c.id} className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-foreground capitalize">{c.credit_type.replace(/_/g, ' ')}</p>
                            <p className="text-xs text-muted-foreground">
                              {t('hire.billingSettings.consumedOfPurchased', { used: c.total_consumed, purchased: c.total_purchased })}
                            </p>
                          </div>
                          <span className="text-lg font-semibold text-foreground">{c.balance}</span>
                        </div>
                      ))
                    )}

                    {!hasUniversalWallet && credits.length === 0 && (
                      <p className="text-sm text-muted-foreground">{t('hire.billingSettings.noCreditsPurchased')}</p>
                    )}

                    {addOnsLoading ? (
                      <Skeleton className="h-14 rounded-lg" />
                    ) : addOnsError ? (
                      <BillingSectionError
                        title={t('hire.billingSettings.addOnsLoadTitle')}
                        message={t('hire.billingSettings.addOnsLoadMessage')}
                        onRetry={() => refetchAddOns()}
                        retryLabel={t('common.retry')}
                      />
                    ) : addOns.map((a) => {
                      const addon = a.add_ons;
                      return (
                        <div key={a.id} className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-foreground">{addon?.name ?? a.service_key.replace(/_/g, ' ')}</p>
                            <p className="text-xs text-muted-foreground">{t('hire.billingSettings.addOnUnits', { remaining: a.units_remaining, purchased: a.units_purchased })}</p>
                          </div>
                          <span className="text-lg font-semibold text-foreground">{a.units_remaining}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
                <Separator className="my-6" />

                <div id="hire-credit-marketplace" className="space-y-4 scroll-mt-24">
              {creditCartItems.length > 0 && (
                    <div ref={creditCartRef} className="rounded-2xl border border-primary/30">
                  <div className="p-5 sm:p-6 pb-3">
                    <h2 className="text-base font-bold font-display text-foreground flex items-center gap-2">
                      <ShoppingCart className="w-4 h-4 text-primary" />{t('hire.billingCredits.cart.title')}
                    </h2>
                    <p className="text-xs text-muted-foreground mt-1">{t('hire.billingCredits.cart.description')}</p>
                  </div>
                  <div className="px-5 sm:px-6 pb-5 sm:pb-6 space-y-3">
                    {creditCartItems.map(({ pack, quantity }) => (
                      <div key={pack.id} className="flex flex-col gap-3 rounded-lg border border-border p-3 text-sm sm:flex-row sm:items-center sm:justify-between">
                        <div className="min-w-0">
                          <p className="font-medium text-foreground">{pack.name}</p>
                          <div className="text-xs text-muted-foreground">
                            {t('hire.billingCredits.cart.eachLabel')}
                            <CreditCartLinePricing quote={creditCartPricing.quote} packId={pack.id} value="unit" />
                          </div>
                        </div>
                        <div className="flex flex-wrap items-center justify-between gap-3 sm:justify-end">
                          <div className="flex items-center gap-1 rounded-full border border-input p-1">
                            <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={() => setCreditPackQuantity(pack.id, quantity - 1)} disabled={creditFlowLocked} aria-label={t('hire.billingCredits.cart.decreasePack', { name: pack.name })}>
                              <Minus className="h-3.5 w-3.5" />
                            </Button>
                            <span className="min-w-8 text-center font-semibold">{quantity}</span>
                            <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={() => setCreditPackQuantity(pack.id, quantity + 1)} disabled={creditFlowLocked} aria-label={t('hire.billingCredits.cart.increasePack', { name: pack.name })}>
                              <Plus className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                          <CreditCartLinePricing quote={creditCartPricing.quote} packId={pack.id} value="line" />
                          <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" onClick={() => setCreditPackQuantity(pack.id, 0)} disabled={creditFlowLocked} aria-label={t('hire.billingCredits.cart.removePack', { name: pack.name })}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                    <CreditCartPricingStatus ready={creditCartPricing.ready} reason={creditCartPricing.reason} />
                    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border pt-3">
                      <div className="font-semibold">{t('hire.billingCredits.total')}<CreditCartPricing quote={creditCartPricing.quote} /></div>
                      <div className="flex gap-2">
                        <Button variant="outline" onClick={clearCreditCart} disabled={creditFlowLocked}>{t('common.clear')}</Button>
                        <Button type="button" onClick={openCreditCheckout} disabled={creditCheckoutLocked}>
                          {creditPurchaseMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin me-1.5" /> : <ShoppingCart className="w-4 h-4 me-1.5" />}
                          {t('hire.billingCredits.cart.checkoutCart')}
                        </Button>
                      </div>
                    </div>
                  </div>
                    </div>
              )}

                  <div>
                    <div className="pb-4">
                  <h2 className="text-base font-bold font-display text-foreground flex items-center gap-2">
                    <Coins className="w-4 h-4 text-primary" />{t('hire.billingCredits.title')}
                  </h2>
                  <p className="text-xs text-muted-foreground mt-1">{t('hire.billingCredits.subtitle')}</p>
                </div>
                    <div>
                  {creditPacksLoading ? (
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                      {[1, 2, 3].map((i) => <Skeleton key={i} className="h-44 rounded-xl" />)}
                    </div>
                  ) : creditPacksError ? (
                    <BillingSectionError
                      title={t('hire.billingCredits.creditPacksLoadTitle')}
                      message={t('hire.billingCredits.creditPacksLoadMessage')}
                      onRetry={() => refetchCreditPacks()}
                      retryLabel={t('common.retry')}
                    />
                  ) : creditPacks.length === 0 ? (
                    <div className="rounded-xl border border-border bg-muted/20 py-8 text-center">
                      <p className="font-semibold text-foreground">{t('hire.billingCredits.noPacksTitle')}</p>
                      <p className="text-sm text-muted-foreground mt-1">{t('hire.billingCredits.noPacksDescription')}</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                      {creditPacks.map((pack) => {
                        const packId = getCreditPackId(pack);
                        const packQuantity = creditCart[packId] ?? 0;
                        return (
                          <Card key={packId} className="rounded-xl transition-shadow hover:shadow-md">
                            <CardHeader className="pb-2">
                              <div className="flex items-start justify-between gap-3">
                                <CardTitle className="text-base">{pack.name}</CardTitle>
                                <div className="flex shrink-0 flex-wrap justify-end gap-1">
                                {packQuantity > 0 && (
                                  <Badge className="border-0 bg-primary/10 text-[10px] text-primary">
                                    {t('hire.billingCredits.cart.inCart', { count: packQuantity })}
                                  </Badge>
                                )}
                                {pack.credits_included >= 200 && (
                                  <Badge className="border-0 bg-primary/10 text-[10px] text-primary">{t('hire.billingCredits.bestValue')}</Badge>
                                )}
                                </div>
                              </div>
                            </CardHeader>
                            <CardContent className="space-y-3">
                            <div className="flex items-baseline gap-1">
                              <span className="text-2xl font-bold text-foreground">{pack.credits_included}</span>
                              <span className="text-sm text-muted-foreground">{t('hire.billingCredits.forkCredits')}</span>
                            </div>
                            <CreditPackGrossPrice platform="forkhire" packId={pack.id} />
                            {pack.valid_days && (
                              <p className="text-xs text-muted-foreground">{t('hire.billingCredits.validForDays', { count: pack.valid_days })}</p>
                            )}
                            <Button type="button" className="w-full rounded-xl" onClick={() => addCreditPackToCart(packId, pack.name)} disabled={creditFlowLocked}>
                              <ShoppingCart className="w-4 h-4 me-1.5" />
                              {packQuantity > 0 ? t('hire.billingCredits.cart.addAnother') : t('hire.billingCredits.addToCart')}
                            </Button>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  )}
                </div>
                  </div>
                </div>
              </Card>
            </div>

            <Card className="shadow-card border-border rounded-2xl overflow-hidden">
              <div className="p-5 sm:p-6 pb-4">
                <h2 className="text-base font-bold font-display text-foreground">{t('hire.billingSettings.usageTitle')}</h2>
                <p className="text-xs text-muted-foreground mt-0.5">{t('hire.billingSettings.usageSubtitle')}</p>
              </div>
              <div className="px-5 sm:px-6 pb-5 sm:pb-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {entitlements.map(({ key, ent, label }) => {
                  const Icon = SERVICE_ICONS[key] ?? BarChart3;
                  const used = ent.used ?? 0;
                  const limit = ent.limit ?? 0;
                  const creditBal = ent.creditBalance ?? 0;
                  const trialExtra = trialExtraAllowances[key];

                  return (
                    <HireUsageServiceCard
                      key={key}
                      icon={Icon}
                      label={label}
                      used={used}
                      limit={limit}
                      isUnlimited={ent.isUnlimited}
                      creditBalance={creditBal}
                      limitTypeLabel={getLimitTypeLabel(ent.limitType)}
                      trialExtra={trialExtra}
                      unlimitedLabel={t('hire.billingSettings.unlimited')}
                      trialBonusLabel={(trialExtra ?? 0) > 0 ? t('hire.billingSettings.trialBonusShort', { count: trialExtra }) : undefined}
                    />
                  );
                })}
              </div>
            </Card>

            <Collapsible>
              <Card className="shadow-card border-border rounded-2xl overflow-hidden">
                <CollapsibleTrigger className="w-full px-5 sm:px-6 py-4 flex items-center justify-between cursor-pointer hover:bg-muted/20 transition-colors rounded-xl">
                  <span className="text-sm font-medium text-foreground">{t('hire.billingSettings.howBillingWorksTitle')}</span>
                  <ChevronDown className="w-4 h-4 text-muted-foreground transition-transform duration-200" />
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="px-5 sm:px-6 pb-5 sm:pb-6 space-y-4 text-sm text-muted-foreground">
                    <Separator />
                    <div>
                      <p className="font-medium text-foreground mb-1">{t('hire.billingSettings.upgradesTitle')}</p>
                      <p>{t('hire.billingSettings.upgradesDescription')}</p>
                    </div>
                    <div>
                      <p className="font-medium text-foreground mb-1">{t('hire.billingSettings.downgradesTitle')}</p>
                      <p>{t('hire.billingSettings.downgradesDescription')}</p>
                    </div>
                    <div>
                      <p className="font-medium text-foreground mb-1">{t('hire.billingSettings.creditsExplainerTitle')}</p>
                      <p>{t('hire.billingSettings.creditsExplainerDescription')}</p>
                    </div>
                  </div>
                </CollapsibleContent>
              </Card>
            </Collapsible>

            <BillingHistoryCard
              entries={billingHistoryEntries}
              brand="forkhire"
              loading={billingHistoryLoading}
              error={billingHistoryError}
              summary
              viewAllTo="/settings/billing/invoices"
              retryLabel={t('common.retry')}
              errorTitle={t('hire.billingSettings.invoicesLoadTitle')}
              errorMessage={t('hire.billingSettings.invoicesLoadMessage')}
              onRetry={refetchBillingHistory}
              onOpenInvoice={(invoice) => setSelectedInvoice(invoice)}
            />
          </>
        )}
      </div>

      <InvoiceViewerDialog
        invoice={selectedInvoice}
        open={!!selectedInvoice}
        onOpenChange={(open) => {
          if (!open) setSelectedInvoice(null);
        }}
      />
      {selectedPurchasePlan && (
        <CheckoutPurchaseDialog
          open={purchaseDialogOpen}
          onOpenChange={(open) => {
            if (!purchaseMutation.isPending) setPurchaseDialogOpen(open);
          }}
          translationScope="hire.checkout"
          brand="hire"
          platform="forkhire"
          planId={selectedPurchasePlan.id}
          planName={selectedPurchasePlan.plan_name}
          billingCycle={purchaseBillingCycle}
          introPrice={selectedPurchaseIntro}
          renewalPrice={selectedPurchasePrice}
          priceAvailable={selectedPurchasePriceAvailable}
          billingConsent={billingConsent}
          onBillingConsentChange={setBillingConsent}
          disabled={purchaseFlowLocked}
          isProcessing={purchaseMutation.isPending}
          confirmLabel={t('hire.checkout.confirmPay')}
          onConfirm={() => {
            setPurchaseDialogOpen(false);
            setPurchaseBillingDialogOpen(true);
          }}
        />
      )}
      {selectedPurchasePlan && (
        <BillingConfirmationDialog
          open={purchaseBillingDialogOpen}
          onOpenChange={(open) => {
            if (!purchaseMutation.isPending) setPurchaseBillingDialogOpen(open);
          }}
          ownerType="organization"
          ownerId={orgId}
          client={supabase}
          editPath="/settings/billing/identity"
          readiness={billingReadiness}
          isReadinessLoading={readinessLoading}
          isProcessing={purchaseMutation.isPending}
          pricingSummary={<PlanPricingBreakdown platform="forkhire" planId={selectedPurchasePlan.id} billingCycle={purchaseBillingCycle} introductory={getHireIntroPrice(selectedPurchasePlan) != null} />}
          pricingReady={selectedPurchasePricingReady}
          pricingLoading={pricingCatalogLoading}
          onConfirm={() => purchaseMutation.mutate()}
        />
      )}
      <ActiveSubscriptionPlanChangeDialog
        open={!!selectedPlanChangeTarget}
        onOpenChange={(open) => {
          if (!open) setSelectedPlanChangeTarget(null);
        }}
        ownerType="organization"
        ownerId={orgId}
        portal="forkhire"
        targetPlan={selectedPlanChangeTarget}
        client={supabase}
        callbackUrl={`${window.location.origin}/settings/billing`}
        editBillingPath="/settings/billing/identity"
        billingReadiness={billingReadiness}
        isBillingReadinessLoading={readinessLoading}
      />
      <SecurePaymentDialog
        open={!!paymentForm && (purchaseState === 'payment' || purchaseState === 'processing')}
        config={paymentForm}
        client={supabase}
        brand="forkhire"
        title={t('hire.checkout.paymentDetails')}
        onBack={() => {
          setPaymentForm(null);
          setPurchaseState('select');
          setPurchaseBillingDialogOpen(true);
        }}
      />
      <SecurePaymentDialog
        open={!!creditPaymentForm}
        config={creditPaymentForm}
        client={supabase}
        brand="forkhire"
        title={t('hire.billingCredits.paymentDetails')}
        onBack={() => setCreditPaymentForm(null)}
      />
      <BillingConfirmationDialog
        key={`credit-confirmation-${creditConfirmationRevision}`}
        open={creditBillingDialogOpen}
        onOpenChange={setCreditBillingDialogOpen}
        ownerType="organization"
        ownerId={orgId}
        client={supabase}
        editPath="/settings/billing/identity"
        readiness={billingReadiness}
        isReadinessLoading={readinessLoading}
        isProcessing={creditPurchaseMutation.isPending}
        pricingSummary={<CreditCartPricing quote={creditCartPricing.quote} breakdown />}
        pricingReady={creditCartPricing.ready}
        pricingLoading={creditCartPricing.reason === 'loading'}
        onConfirm={() => creditPurchaseMutation.mutate()}
      />
    </AppLayout>
  );
}
